import React, { useEffect } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import Moment from 'moment'
import I18n from 'APP/I18n'
import { SafeAreaView } from 'react-native-safe-area-context'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import type { Dispatch } from 'redux'

// Actions
import StartupActions from 'APP/Redux/StartupRedux'

// Components
import AlertModal from 'APP/Components/AlertModal'

// Services
import Analytics from 'APP/Services/Analytics'

import type { RootState } from 'APP/Store/CreateStore'

type LocalizedText = Record<string, string>

type ImportantCommunicationBannerContent = {
  title: LocalizedText
  subtitle: LocalizedText
  // Optional allowlist of service ids; when present, the banner only shows to
  // users with at least one of these services in their eligibleServices.
  services?: string[]
}

type BannerName = 'BusierThanUsual' | null

const mapStateToProps = (state: RootState) => {
  const { app, patient, features } = state
  const userId = patient?.profile?.id
  const userServices = patient?.profile?.eligibleServices || {}
  // @ts-expect-error FIXME: handle falsy values for userId
  const userSettings = (app.userSettings && app.userSettings[userId]) || {}

  const importantCommunicationBannerContent = features.importantCommunicationBannerContent as
    | ImportantCommunicationBannerContent
    | undefined

  // Show the "busier than usual" banner when:
  //   1. LaunchDarkly has provided non-empty banner content, AND
  //   2. the user hasn't dismissed it yet today, AND
  //   3. the content isn't restricted to specific services, or the user has
  //      at least one of those services.
  // Dismissal stores today's local midnight timestamp in `lastBusierThanUsualModal`,
  // so after local midnight the equality breaks and the banner reappears.
  const bannerServices = importantCommunicationBannerContent?.services
  const isUserEligibleForBanner =
    !bannerServices ||
    bannerServices.length === 0 ||
    bannerServices.some((serviceId) => userServices[serviceId] !== undefined)

  const shouldShowBusierThanUsualBanner = Boolean(
    importantCommunicationBannerContent &&
      Object.keys(importantCommunicationBannerContent).length > 0 &&
      isUserEligibleForBanner &&
      !(Moment().startOf('day').valueOf() === userSettings.lastBusierThanUsualModal)
  )

  const bannerName: BannerName = shouldShowBusierThanUsualBanner ? 'BusierThanUsual' : null

  return {
    userId,
    shouldShowBusierThanUsualBanner,
    bannerName,
    importantCommunicationBannerContent,
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setBusierThanUsualBannerHidden: (userId: string, timestamp: number) =>
      dispatch(StartupActions.setBusierThanUsualBannerHidden(userId, timestamp)),
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type Props = ConnectedProps<typeof connector>

const Banner = ({
  userId,
  shouldShowBusierThanUsualBanner,
  bannerName,
  importantCommunicationBannerContent,
  setBusierThanUsualBannerHidden,
}: Props) => {
  useEffect(() => {
    if (bannerName) {
      const bannerPayload = { banner_name: bannerName }
      Analytics.trackEvent('banner_view', bannerPayload)
      Analytics.attachEntity('banner_context', bannerPayload)
    } else {
      Analytics.detachEntity('banner_context')
    }
    // Mirrors the previous class component's componentDidMount/componentDidUpdate
    // behavior: only re-run when bannerName changes.
  }, [bannerName])

  const handleDismissBusierThanUsual = () => {
    // @ts-expect-error FIXME: handle falsy values for userId
    setBusierThanUsualBannerHidden(userId, Moment().startOf('day').valueOf())
  }

  const localeKey: 'en' | 'fr' = I18n.locale === 'fr' ? 'fr' : 'en'

  if (shouldShowBusierThanUsualBanner) {
    const busierThanUsualBannerProps = {
      alertStyle: 1,
      handleDismissModal: handleDismissBusierThanUsual,
      titleText: importantCommunicationBannerContent?.title?.[localeKey],
      subtitleText: importantCommunicationBannerContent?.subtitle?.[localeKey],
    }
    return <AlertModal key="BusierThanUsual" {...busierThanUsualBannerProps} />
  }

  return <SafeAreaView edges={isEmbeddedEnvironment() ? [] : ['top']} />
}

export default connector(Banner)
